import {Map as GeomaticoMap, type Viewport} from '@geomatico/geocomponents';
import type {FeatureCollection, Point} from 'geojson';
import type {LayerSpecification, MapLayerMouseEvent} from 'maplibre-gl';
import {type FC, useMemo, useRef, useState} from 'react';
// @ts-expect-error
import type {MapRef} from 'react-map-gl';
import ForestMassPopup from '@/components/ForestMassList/ForestMassPopup';
import {INITIAL_MAPSTYLE_URL, INITIAL_VIEWPORT} from '@/config';
import {type ForestMassStatus, STATUS} from '@/domain/entities/ForestMassStatus';
import {secondaryColor} from '@/theme/theme';

type Props = {
  forestMassesStatuses: Array<ForestMassStatus>;
  onForestMassClick: (uuid: string) => void;
};

const ForestMassesMap: FC<Props> = ({forestMassesStatuses, onForestMassClick}) => {
  const mapRef = useRef<MapRef>(null);
  const [viewport, setViewport] = useState<Viewport>(INITIAL_VIEWPORT);
  const [clickedFeature, setClickedFeature] = useState<ForestMassStatus | undefined>(undefined);

  const geojson: FeatureCollection<Point> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: forestMassesStatuses.map((forestMass) => {
        const {position, ...properties} = forestMass;
        return {
          type: 'Feature',
          geometry: position,
          properties: {
            color: getGlobalStatusColor(properties.finsaStatus, properties.aseforStatus, properties.fortopStatus),
            ...properties,
          },
        };
      }),
    }),
    [forestMassesStatuses],
  );

  const sources = {
    centroids: {
      type: 'geojson' as const,
      data: geojson,
    },
  };

  const handleClick = (e: MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    if (feature) {
      const selectedForestMass = forestMassesStatuses.find(
        (f) => f.forestMassUuid === feature.properties.forestMassUuid,
      );
      if (selectedForestMass) {
        setClickedFeature(selectedForestMass);
      }
    } else {
      setClickedFeature(undefined);
    }
  };

  return (
    <GeomaticoMap
      ref={mapRef}
      mapStyle={INITIAL_MAPSTYLE_URL}
      viewport={viewport}
      onViewportChange={setViewport}
      sources={sources}
      layers={layers}
      onClick={handleClick}
      interactiveLayerIds={['boundary-fill']}
    >
      {clickedFeature && (
        <ForestMassPopup
          forestMassStatus={clickedFeature}
          onClose={() => setClickedFeature(undefined)}
          onClick={() => onForestMassClick(clickedFeature.forestMassUuid)}
        />
      )}
    </GeomaticoMap>
  );
};

export default ForestMassesMap;

const getGlobalStatusColor = (finsaStatus?: STATUS, aseforStatus?: STATUS, fortopStatus?: STATUS) => {
  if (
    finsaStatus === STATUS.FOREST_MASS_CREATED &&
    aseforStatus === STATUS.PENDING_PLOT_INVENTORIES &&
    fortopStatus === STATUS.PENDING_UAV_FLIGHT
  ) {
    return 'red';
  }
  if (finsaStatus === STATUS.CUT_FORCED) {
    return 'black';
  }
};

const layers: Array<LayerSpecification> = [
  {
    id: 'boundary-fill',
    type: 'circle',
    source: 'centroids',
    paint: {
      'circle-color': ['get', 'color'],
      'circle-opacity': 0.3,
      'circle-stroke-color': secondaryColor,
      'circle-stroke-width': 1,
    },
  },
];
